import * as React from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Circle,
  Divider,
  Flex,
  HStack,
  Icon,
  Text,
  useToast,
  keyframes,
} from "@chakra-ui/react";
import {
  EmrInProcess,
  EmrPillsBottle,
  EmrRegistration,
  SiChevronLeft,
  SiTask,
} from "@medstonetech/slate-icons";
import { ChartSection, ChartSectionHeader } from "modules";
import {
  ChartRouteBaseParams,
  nextRoutesNurse,
  nextRoutesProvider,
  TriageIVMedicationForm,
  TriageIVMedicationSectionByType,
} from "modules/charts-shared";
import {
  useIvAndMedication,
  useUpdateIvAndMedication,
} from "modules/triage/api";
import {
  formToPayload,
  responseToForm,
} from "modules/triage/utils/iv-medications";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import { Loading } from "shared";
import { extractApiErrorMessage } from "utils";
import { formMessages } from "messages";
import { IvAndMedicationsColumn } from "./IvAndMedicationColumn";
import { useRealtimeIvMedications } from "../../hooks/useRealtimeIvMedications";
import type { DataChangeMessage } from "../../hooks/useRealtimeIvMedications";
import { UserPresenceIndicator } from "./UserPresenceIndicator";
import { useAuth0 } from "@auth0/auth0-react";
import { USERID_CLAIMS } from "system-constants";

import { OrderStatus } from "enums";
import { MedsAndIvSummary } from "./MedsAndIvSummary";

import { useHandlerBeforeSessionTimeout } from "hooks";

const SECTION_CODE = "SIVM";
const INVALID_ORDER_STATUSES = ["CancellationRequested", "Cancelled"];

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.2;
  }
`;

export type MedsAndIvTypes = "ivStarts" | "ivMedications" | "medications";

function IvAndMedications(props: {
  chartCode: string;
  isReadOnly?: boolean;
  onBack?: () => void;
}) {
  const { encounterId = "", "*": chartName = "" } =
    useParams<ChartRouteBaseParams>();
  const { user } = useAuth0();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formContext = useForm<TriageIVMedicationForm>({
    defaultValues: {
      ivMedications: {},
      ivStarts: {},
      medications: {},
      treatments: {},
    },
  });
  const {
    watch,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = formContext;
  const { data: ivMedicationData, isLoading: getIsLoading } =
    useIvAndMedication(encounterId);
  const { mutateAsync: updateIvMedication, isLoading: updateIsLoading } =
    useUpdateIvAndMedication(encounterId);
  const { chartCode, isReadOnly, onBack } = props;

  const currentUserId = user ? user[USERID_CLAIMS] : "";

  const [nextRoute, setNextRoute] = useState<string | null>(null);
  const [remoteChangeNotification, setRemoteChangeNotification] =
    useState<DataChangeMessage | null>(null);
  const lastSnapshotCreatedAtRef = useRef<string | null>(null);
  const isDirtyRef = useRef(isDirty);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const handleRemoteDataChange = useCallback(
    (change: DataChangeMessage) => {
      if (change.changedByUserId === currentUserId) {
        return;
      }

      if (isDirtyRef.current) {
        setRemoteChangeNotification(change);
      } else {
        queryClient.invalidateQueries(["ivAndMedication", { encounterId }]);
        queryClient.invalidateQueries([
          "ivAndMedicationCounters",
          { encounterId },
        ]);
        queryClient.invalidateQueries(["medsAndIvCounters", { encounterId }]);
        queryClient.invalidateQueries(["treatmentsCounters", { encounterId }]);
      }
    },
    [currentUserId, encounterId, queryClient]
  );

  const handleReconnect = useCallback(() => {
    queryClient.invalidateQueries(["ivAndMedication", { encounterId }]);
    queryClient.invalidateQueries(["ivAndMedicationCounters", { encounterId }]);
    queryClient.invalidateQueries(["medsAndIvCounters", { encounterId }]);
    queryClient.invalidateQueries(["treatmentsCounters", { encounterId }]);
  }, [encounterId, queryClient]);

  const { connectionStatus, connectedUsers, editingLocks, sendEditingState } =
    useRealtimeIvMedications({
      encounterId,
      chartCode,
      sectionCode: "SIVM",
      onDataChange: handleRemoteDataChange,
      onReconnect: handleReconnect,
    });

  const handleRefreshFromRemote = useCallback(async () => {
    await queryClient.invalidateQueries(["ivAndMedication", { encounterId }]);
    await queryClient.invalidateQueries([
      "ivAndMedicationCounters",
      { encounterId },
    ]);
    await queryClient.invalidateQueries(["medsAndIvCounters", { encounterId }]);
    await queryClient.invalidateQueries([
      "treatmentsCounters",
      { encounterId },
    ]);
    setRemoteChangeNotification(null);
    toast.closeAll();
  }, [encounterId, queryClient, toast]);

  const remoteNotification = useMemo(() => {
    if (!remoteChangeNotification) return null;
    return (
      <Alert status="warning" borderRadius="md" mb={2} fontSize="sm">
        <AlertIcon />
        <Box flex={1}>
          {remoteChangeNotification.changedByUserName || "Another user"} updated
          Meds &amp; IV data.
        </Box>
        <Button size="sm" colorScheme="blue" onClick={handleRefreshFromRemote}>
          Refresh
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setRemoteChangeNotification(null);
            toast.closeAll();
          }}
        >
          Dismiss
        </Button>
      </Alert>
    );
  }, [remoteChangeNotification, handleRefreshFromRemote, toast]);

  const location = useLocation();
  const fromSummary = location.pathname.includes("medical-records");

  useEffect(() => {
    if (ivMedicationData) {
      lastSnapshotCreatedAtRef.current =
        ivMedicationData.data.lastSnapshotCreatedAt ?? null;
      reset(responseToForm(ivMedicationData.data));
    }
  }, [ivMedicationData, reset]);

  useEffect(() => {
    if (nextRoute && !isDirty) {
      navigate(nextRoute);
      setNextRoute(null);
    }
  }, [nextRoute, navigate, isDirty]);

  const ivStarts = watch("ivStarts");
  const ivMedications = watch("ivMedications");
  const medications = watch("medications");

  const onSubmit = useCallback(
    async (formData: TriageIVMedicationForm) => {
      const payload = {
        ...formToPayload(formData),
        lastSnapshotCreatedAt: lastSnapshotCreatedAtRef.current,
      };
      try {
        await updateIvMedication(payload);
        toast({ description: formMessages.updateSuccess("Meds & IV") });
        reset(formData);
      } catch (error) {
        toast({ description: extractApiErrorMessage(error) });
        throw error;
      }
    },
    [updateIvMedication, toast, reset]
  );

  const handleSaveAndNext = () => {
    const nextRoutes =
      chartCode[0] === "T" ? nextRoutesNurse : nextRoutesProvider;
    const route = nextRoutes[nextRoutes.indexOf(chartName || "") + 1];
    handleSubmit(onSubmit)().then(() => {
      setNextRoute(`./../${route}`);
    });
  };

  const filteredItems: TriageIVMedicationSectionByType[] = React.useMemo(() => {
    const ivStartsArr = Object.values(ivStarts ?? {}).map((x) => ({
      ...x,
      type: "ivStarts",
    }));
    const ivMedicationsArr = Object.values(ivMedications ?? {}).map((x) => ({
      ...x,
      type: "ivMedications",
    }));
    const medicationsArr = Object.values(medications ?? {}).map((x) => ({
      ...x,
      type: "medications",
    }));

    const allElements = [
      ...ivStartsArr,
      ...ivMedicationsArr,
      ...medicationsArr,
    ];

    return allElements.filter(
      (el) => !INVALID_ORDER_STATUSES.includes(el.order?.status ?? "")
    );
  }, [ivStarts, ivMedications, medications]);

  const columns = React.useMemo(
    () => ({
      ordered: {
        title: "Ordered",
        icon: EmrRegistration,
        bg: "orange",
        status: "Ordered" as OrderStatus,
        items: filteredItems.filter(
          (item) =>
            item?.status === "Ordered" ||
            item?.status === "PRN" ||
            item?.status === "Prepared"
        ),
        counter: filteredItems.filter(
          (item) =>
            item?.status !== "PRN" &&
            item?.scheduled === "Ordered" &&
            (item?.status === "Ordered" || item?.status === "Prepared")
        ).length,
      },
      inProcess: {
        title: "In Process",
        icon: EmrInProcess,
        bg: "blue",
        status: "InProcess" as OrderStatus,
        items: filteredItems.filter((item) => item?.status === "InProcess"),
        counter: filteredItems.filter(
          (item) =>
            item?.status !== "PRN" &&
            item?.scheduled === "Ordered" &&
            item?.status === "InProcess"
        ).length,
      },
      completed: {
        title: "Completed",
        icon: EmrInProcess,
        bg: "green",
        status: "Completed" as OrderStatus,
        items: filteredItems.filter((item) => item?.status === "Completed"),
        counter: filteredItems.filter(
          (item) =>
            item?.status !== "PRN" &&
            item?.scheduled === "Ordered" &&
            item?.status === "Completed"
        ).length,
      },
      administered: {
        title: "Administered",
        icon: SiTask,
        bg: "purple",
        status: "Administered" as OrderStatus,
        items: filteredItems.filter((item) => item?.status === "Administered"),
        counter: filteredItems.filter(
          (item) =>
            item?.status !== "PRN" &&
            item?.scheduled === "Ordered" &&
            item?.status === "Administered"
        ).length,
      },
    }),
    [filteredItems]
  );

  useHandlerBeforeSessionTimeout(async () => {
    if (isDirty) {
      await handleSubmit(onSubmit)();
    }
  });

  if (getIsLoading) {
    return <Loading />;
  }

  return (
    <Box height="100%" w="100%">
      {Boolean(onBack) && (
        <Button
          variant="label"
          ml="20px"
          color="blue"
          mt="20px"
          onClick={onBack}
        >
          <SiChevronLeft size={2} />
          Back
        </Button>
      )}
      <Flex height="100%" w="100%">
        <FormProvider {...formContext}>
          <ChartSection
            minWidth="100%"
            onSubmit={handleSubmit(onSubmit)}
            isDirty={isDirty}
            disabled={isReadOnly}
            onRouteChangeSave={async (onComplete) => {
              if (isDirty) {
                await handleSubmit(onSubmit)();
              }
              onComplete();
            }}
          >
            <ChartSectionHeader
              icon={<Icon as={EmrPillsBottle} />}
              chartId={chartCode}
              showNotesBtn={false}
              encounterId={encounterId}
              sectionId={SECTION_CODE}
              showActionsBtns
              showAuditsBtns
              isLoading={updateIsLoading}
              onSaveAndNext={handleSaveAndNext}
              sectionLabel="Meds & IV"
              useWebsocket
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box>Meds &amp; IV {isReadOnly ? "(Read Only)" : ""}</Box>
                {connectionStatus === "connected" ? (
                  <UserPresenceIndicator
                    connectedUsers={connectedUsers}
                    currentUserId={currentUserId}
                  />
                ) : connectionStatus === "connecting" ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Circle
                      size="8px"
                      bg="blue.400"
                      sx={{ animation: `${pulse} 1.5s ease-in-out infinite` }}
                    />
                  </Box>
                ) : null}
              </Box>
            </ChartSectionHeader>

            {connectionStatus === "connecting" && !isReadOnly && (
              <Alert
                status="info"
                borderRadius="md"
                mb={4}
                fontSize="md"
                color="white"
              >
                <AlertIcon color="white" />
                Connecting to real-time synchronization...
              </Alert>
            )}
            {connectionStatus === "disconnected" && !isReadOnly && (
              <Alert
                status="error"
                borderRadius="md"
                mb={4}
                fontSize="md"
                color="white"
              >
                <AlertIcon color="white" />
                Disconnected from real-time synchronization
              </Alert>
            )}

            {remoteNotification}

            <Box w="100%" display="flex" justifyContent="space-between">
              {Object.entries(columns).map(([_key, column], index) => {
                return (
                  <React.Fragment key={column.status}>
                    <IvAndMedicationsColumn
                      header={
                        <Box p={4} bg={column.bg} borderRadius="md">
                          <HStack>
                            <Icon as={column.icon} color="white" boxSize={5} />
                            <Box
                              color="white"
                              fontWeight="bold"
                              width="100%"
                              display="flex"
                              justifyContent="space-between"
                            >
                              <Text fontSize="lg">{column.title}</Text>
                              <Text fontSize="lg">{column.counter}</Text>
                            </Box>
                          </HStack>
                        </Box>
                      }
                      status={column.status}
                      items={column.items}
                      onSave={handleSubmit(onSubmit)}
                      isReadOnly={isReadOnly}
                      editingLocks={editingLocks}
                      sendEditingState={sendEditingState}
                    />
                    {index < Object.keys(columns).length - 1 && (
                      <Divider orientation="vertical" minH="90vh" />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>

            {fromSummary &&
              filteredItems.map((x) => (
                <MedsAndIvSummary key={x.orderId} item={x} />
              ))}
          </ChartSection>
        </FormProvider>
      </Flex>
    </Box>
  );
}

export { IvAndMedications };
